import type { RuntimeTransportLookupRouteResponse } from "@wakeloop/schemas/types";
import { buildDaemonRouteObservationPresentation } from "@wakeloop/start";
import {
  DaemonRouteObservationFetchError,
  fetchDaemonRouteObservation,
} from "../config/daemon-route-observation-api";
import type {
  DaemonServiceRuntimeStatus,
  DaemonStatus,
} from "../config/daemon-state";
import { getDaemonStatus } from "../config/daemon-state";
import type { WakeLoopProfile } from "../config/wakeloop-profiles-api";
import { buildWakeLoopRequestContextFromProfile } from "../config/wakeloop-request-context";
import { resolveCurrentDaemonExpectedVersion } from "../local-service/lifecycle/current-daemon-version";
import type { DaemonServiceInventory } from "../local-service/lifecycle/daemon-service-inventory";
import { inspectDaemonServiceInventory } from "../local-service/lifecycle/daemon-service-inventory";
import type {
  DaemonServiceManager,
  DaemonServiceManagerRuntimeStatus,
} from "../local-service/lifecycle/daemon-service-manager";
import {
  resolveDaemonRuntimeState,
  resolveDaemonServiceManagerForRuntimeState,
} from "../local-service/lifecycle/daemon-service-runtime";
import { resolveLocalServiceDemand } from "../local-service/lifecycle/local-service-demand";
import type { DeviceRuntimeStateSyncResult } from "../local-service/reporting/device-runtime-state-sync";
import { syncDeviceRuntimeStateProjection } from "../local-service/reporting/device-runtime-state-sync";
import { resolveProfileWorkspaceRuntime } from "../profile-workspace/runtime-resolution";
import type {
  OpenClawDiagnosticsMode,
  OpenClawLocalDiagnosticsState,
} from "../provider-hosts/openclaw/diagnostics";
import {
  createOpenClawLocalDiagnosticsFailure,
  resolveOpenClawLocalDiagnostics,
} from "../provider-hosts/openclaw/diagnostics";
import { resolveAgentProfilePrimaryAgentControllerReport } from "./agent-profile-primary-binding";
import {
  createExpectedRoutesReadSession,
  type ExpectedRoutesReadSession,
} from "./expected-routes-read-session";
import type {
  GatewayChainProfile,
  GatewayChainReadiness,
} from "./gateway-chain-readiness";
import { resolveGatewayChainReadiness } from "./gateway-chain-readiness";
import {
  createPrimaryBindingReadSession,
  type PrimaryBindingReadSession,
} from "./primary-binding-read-session";
import {
  type AgentReplyReadinessStatus,
  type DeviceReplyReadinessStatus,
  isOpenClawAgentControllerReport,
  resolveAgentReplyReadinessStatus,
  resolveDeviceReplyReadinessStatus,
} from "./reply-readiness";

export type CurrentReplyReadinessSnapshot = {
  daemonRouteObservation: DaemonRouteObservation;
  observedAt: string;
  daemonStatus: DaemonStatus;
  inventory: DaemonServiceInventory | null;
  runtimeProjection: DeviceRuntimeStateSyncResult | null;
  runtimeStatus: DaemonServiceRuntimeStatus | null;
  displayRuntimeStatus: DaemonServiceRuntimeStatus | null;
  serviceManagerStatus: DaemonServiceManagerRuntimeStatus | null;
  gatewayChainReadiness: GatewayChainReadiness | null;
  deviceReplyReadiness: DeviceReplyReadinessStatus;
  agentReplyReadiness: AgentReplyReadinessStatus | null;
  openClawLocalDiagnostics: OpenClawLocalDiagnosticsState | null;
  profileWorkspaceRuntimeResolution: ReturnType<
    typeof resolveProfileWorkspaceRuntime
  > extends Promise<infer TValue>
    ? TValue | null
    : never;
};

export type DaemonRouteObservation =
  | {
      detail: null;
      observedAt: string;
      profileId: string;
      reasonCode: "online";
      route: RuntimeTransportLookupRouteResponse;
      status: "online";
    }
  | {
      detail: string;
      observedAt: string;
      profileId: string;
      reasonCode: "route.not_registered";
      route: RuntimeTransportLookupRouteResponse;
      status: "offline";
    }
  | {
      detail: string;
      observedAt: string;
      profileId: string | null;
      reasonCode:
        | "lookup_failed"
        | "not_requested"
        | "profile_not_agent"
        | "profile_not_selected"
        | "server_unsupported";
      route: null;
      status: "unavailable";
    };

export async function resolveCurrentReplyReadinessSnapshot(
  input: {
    baseUrl?: string;
    checkGatewayChain?: boolean;
    gatewayChainProfile?: GatewayChainProfile | null;
    gatewayChainTimeoutMs?: number;
    includeDaemonRouteObservation?: boolean;
    openClawDiagnosticsMode?: OpenClawDiagnosticsMode | null;
    ownerUserId?: string | null;
    profile?: WakeLoopProfile | null;
    runtimeProjection?: DeviceRuntimeStateSyncResult | null;
    runtimeWorkdir?: string;
  } = {}
): Promise<CurrentReplyReadinessSnapshot> {
  const daemonStatus = await getDaemonStatus();
  // One snapshot = one binding read per profile: the runtime projection
  // check, the local-service demand catalog, and the agent readiness lookup
  // all share this operation-scoped session instead of re-fanning out.
  const bindingReads = createPrimaryBindingReadSession({
    ...(input.baseUrl ? { baseUrl: input.baseUrl } : {}),
  });
  const expectedRoutesRead = createExpectedRoutesReadSession({
    ...(input.baseUrl ? { baseUrl: input.baseUrl } : {}),
  });
  const runtimeProjectionPromise = resolveRuntimeProjectionForSnapshot({
    bindingReads,
    daemonStatus,
    expectedRoutesRead,
    runtimeProjection: input.runtimeProjection,
  });
  const runtimeStatusPromise = resolveDaemonRuntimeState(daemonStatus).catch(
    () => null
  );
  const inventoryPromise = inspectDaemonServiceInventory({
    expectedVersion: resolveCurrentDaemonExpectedVersion(),
    intent: "status_cleanup",
  }).catch(() => null);
  const profileWorkspaceRuntimeResolutionPromise =
    resolveProfileWorkspaceRuntimeForSnapshot(input.profile ?? null);
  const runtimeStatus = await runtimeStatusPromise;
  const { serviceManager, serviceManagerStatus } =
    await resolveDaemonServiceManagerForRuntimeState({
      runtimeState: runtimeStatus,
      status: daemonStatus,
    }).catch(() => ({
      serviceManager: null,
      serviceManagerStatus: null,
      legacySharedService: null,
    }));
  const [runtimeProjection, inventory, profileWorkspaceRuntimeResolution] =
    await Promise.all([
      runtimeProjectionPromise,
      inventoryPromise,
      profileWorkspaceRuntimeResolutionPromise,
    ]);
  const displayRuntimeStatus = enrichRuntimeStateWithManager({
    runtimeState: runtimeStatus,
    serviceManager,
    serviceManagerStatus,
  });
  const localDemand = await resolveLocalServiceDemand({
    bindingReads,
    expectedRoutesRead,
    ownerUserId: input.ownerUserId ?? input.profile?.ownerUserId ?? null,
    projection: runtimeProjection,
    runtimeWorkdir: input.runtimeWorkdir,
  }).catch(() => null);
  const localDeviceReplyReadiness = await resolveDeviceReplyReadinessStatus({
    baseUrl: input.baseUrl,
    checkGatewayChain: false,
    gatewayChainProfile: input.gatewayChainProfile,
    gatewayChainReadiness: null,
    gatewayChainTimeoutMs: input.gatewayChainTimeoutMs,
    inventory,
    localDemand,
    ownerUserId: input.ownerUserId ?? input.profile?.ownerUserId ?? null,
    profile: input.profile ?? null,
    profileWorkspaceRuntimeResolution,
    runtimeProjection,
    runtimeStatus: displayRuntimeStatus ?? runtimeStatus,
    runtimeWorkdir: input.runtimeWorkdir,
    status: daemonStatus,
  }).catch(() =>
    createFallbackDeviceReplyReadiness({
      daemonStatus,
      runtimeStatus: displayRuntimeStatus ?? runtimeStatus,
    })
  );
  const shouldProbeGatewayChain =
    input.checkGatewayChain === true &&
    !isWakeableLocalReplyReady(localDeviceReplyReadiness) &&
    !localDeviceReplyReadiness.reasonCodes.some(
      (reasonCode) =>
        reasonCode === "service.drifted" ||
        reasonCode === "service.not_installed" ||
        reasonCode === "service.not_running" ||
        reasonCode === "workspace.invalid" ||
        reasonCode === "workspace.missing"
    );
  const gatewayChainReadiness = shouldProbeGatewayChain
    ? await resolveGatewayChainReadiness({
        baseUrl: input.baseUrl,
        profile:
          input.gatewayChainProfile ??
          (input.profile
            ? {
                wakeLoopProfileId: input.profile.wakeLoopProfileId,
                profileName: input.profile.profileName,
              }
            : null),
        timeoutMs: input.gatewayChainTimeoutMs,
      }).catch(() => null)
    : null;
  const deviceReplyReadiness = localDeviceReplyReadiness;
  const openClawLocalDiagnostics =
    await resolveOpenClawLocalDiagnosticsForSnapshot({
      baseUrl: input.baseUrl,
      bindingReads,
      mode: input.openClawDiagnosticsMode ?? null,
      profile: input.profile ?? null,
      profileWorkspaceRuntimeResolution,
    });
  const daemonRouteObservation = await resolveDaemonRouteObservation({
    baseUrl: input.baseUrl,
    includeDaemonRouteObservation: input.includeDaemonRouteObservation === true,
    profile: input.profile ?? null,
  });
  const agentReplyReadiness =
    input.profile?.profileKind === "agent"
      ? await resolveAgentReplyReadinessStatus({
          bindingReads,
          coreWakeRouteOnline: daemonRouteObservation.status === "online",
          deviceReplyReadiness,
          openClawLocalDiagnostics,
          profile: input.profile,
          profileWorkspaceRuntimeResolution,
        }).catch(() => null)
      : null;

  return {
    daemonRouteObservation,
    observedAt: new Date().toISOString(),
    daemonStatus,
    inventory,
    runtimeProjection,
    runtimeStatus,
    displayRuntimeStatus,
    serviceManagerStatus,
    gatewayChainReadiness,
    deviceReplyReadiness,
    agentReplyReadiness,
    openClawLocalDiagnostics,
    profileWorkspaceRuntimeResolution,
  };
}

function isWakeableLocalReplyReady(
  localDeviceReplyReadiness: DeviceReplyReadinessStatus
): boolean {
  return (
    localDeviceReplyReadiness.replyReadiness === "ready" &&
    localDeviceReplyReadiness.wakeableRouteCount > 0
  );
}

function resolveRuntimeProjectionForSnapshot(input: {
  bindingReads: PrimaryBindingReadSession;
  daemonStatus: DaemonStatus;
  expectedRoutesRead: ExpectedRoutesReadSession;
  runtimeProjection?: DeviceRuntimeStateSyncResult | null;
}): Promise<DeviceRuntimeStateSyncResult | null> {
  if (!input.daemonStatus.installed) {
    return Promise.resolve(null);
  }
  if (input.runtimeProjection !== undefined) {
    return Promise.resolve(input.runtimeProjection);
  }
  return syncDeviceRuntimeStateProjection({
    bindingReads: input.bindingReads,
    expectedRoutesRead: input.expectedRoutesRead,
    mode: "check",
  }).catch(() => null);
}

function resolveProfileWorkspaceRuntimeForSnapshot(
  profile: WakeLoopProfile | null
): Promise<CurrentReplyReadinessSnapshot["profileWorkspaceRuntimeResolution"]> {
  if (profile?.profileKind !== "agent") {
    return Promise.resolve(null);
  }
  return resolveProfileWorkspaceRuntime({
    wakeLoopProfileId: profile.wakeLoopProfileId,
    mode: "inspect",
  });
}

async function resolveOpenClawLocalDiagnosticsForSnapshot(input: {
  baseUrl?: string;
  bindingReads: PrimaryBindingReadSession;
  mode: OpenClawDiagnosticsMode | null;
  profile: WakeLoopProfile | null;
  profileWorkspaceRuntimeResolution: CurrentReplyReadinessSnapshot["profileWorkspaceRuntimeResolution"];
}): Promise<OpenClawLocalDiagnosticsState | null> {
  if (
    input.profile?.profileKind !== "agent" ||
    !input.mode ||
    input.profileWorkspaceRuntimeResolution?.status !== "ready"
  ) {
    return null;
  }

  const primaryAgentControllerReport =
    await resolveAgentProfilePrimaryAgentControllerReport({
      baseUrl: input.baseUrl,
      bindingReads: input.bindingReads,
      profile: input.profile,
    }).catch(() => null);
  if (!isOpenClawAgentControllerReport(primaryAgentControllerReport ?? {})) {
    return null;
  }

  return resolveOpenClawLocalDiagnostics({
    mode: input.mode,
    profile: input.profile,
    providerRuntimeWorkingDirectory:
      input.profileWorkspaceRuntimeResolution.effectiveRuntimeWorkingDirectory,
  }).catch((error) => createOpenClawLocalDiagnosticsFailure(error));
}

async function resolveDaemonRouteObservation(input: {
  baseUrl?: string;
  includeDaemonRouteObservation: boolean;
  profile: WakeLoopProfile | null;
}): Promise<DaemonRouteObservation> {
  const observedAt = new Date().toISOString();
  if (!input.includeDaemonRouteObservation) {
    return {
      detail: buildCanonicalUnavailableRouteObservationDetail({
        observedAt,
        reasonCode: "not_requested",
      }),
      observedAt,
      profileId: input.profile?.wakeLoopProfileId ?? null,
      reasonCode: "not_requested",
      route: null,
      status: "unavailable",
    };
  }

  if (!input.profile) {
    return {
      detail: buildCanonicalUnavailableRouteObservationDetail({
        observedAt,
        reasonCode: "profile_not_selected",
      }),
      observedAt,
      profileId: null,
      reasonCode: "profile_not_selected",
      route: null,
      status: "unavailable",
    };
  }

  if (input.profile.profileKind !== "agent") {
    return {
      detail: buildCanonicalUnavailableRouteObservationDetail({
        observedAt,
        reasonCode: "profile_not_agent",
      }),
      observedAt,
      profileId: input.profile.wakeLoopProfileId,
      reasonCode: "profile_not_agent",
      route: null,
      status: "unavailable",
    };
  }

  try {
    const response = await fetchDaemonRouteObservation({
      requestContext: buildWakeLoopRequestContextFromProfile({
        wakeLoopProfile: input.profile,
      }),
      baseUrl: input.baseUrl,
      profileId: input.profile.wakeLoopProfileId,
    });
    if (response.route.online) {
      return {
        detail: null,
        observedAt: response.observedAt,
        profileId: response.profileId,
        reasonCode: "online",
        route: response.route,
        status: "online",
      };
    }
    return {
      detail: `Server can't currently see an active reply route for this agent profile (server reported ${response.route.reasonCode}).`,
      observedAt: response.observedAt,
      profileId: response.profileId,
      reasonCode: "route.not_registered",
      route: response.route,
      status: "offline",
    };
  } catch (error) {
    const reasonCode =
      error instanceof DaemonRouteObservationFetchError
        ? error.code
        : "lookup_failed";
    const detail =
      reasonCode === "server_unsupported"
        ? buildCanonicalUnavailableRouteObservationDetail({
            observedAt,
            reasonCode: "server_unsupported",
          })
        : buildLookupFailedRouteObservationDetail({
            error,
            observedAt,
          });
    return {
      detail,
      observedAt,
      profileId: input.profile.wakeLoopProfileId,
      reasonCode,
      route: null,
      status: "unavailable",
    };
  }
}

function buildCanonicalUnavailableRouteObservationDetail(input: {
  observedAt: string;
  reasonCode:
    | "lookup_failed"
    | "not_requested"
    | "profile_not_agent"
    | "profile_not_selected"
    | "server_unsupported";
}): string {
  const detail = buildDaemonRouteObservationPresentation({
    observedAt: input.observedAt,
    reasonCode: input.reasonCode,
    status: "unavailable",
  }).detail;
  if (!detail) {
    throw new Error(
      `missing unavailable reply route detail for ${input.reasonCode}`
    );
  }
  return detail;
}

function buildLookupFailedRouteObservationDetail(input: {
  error: unknown;
  observedAt: string;
}): string {
  const detail = buildCanonicalUnavailableRouteObservationDetail({
    observedAt: input.observedAt,
    reasonCode: "lookup_failed",
  });
  const rawError = toErrorMessage(input.error).trim();
  if (rawError.length === 0 || rawError === detail) {
    return detail;
  }
  return `${detail} Raw lookup error: ${rawError}`;
}

function createFallbackDeviceReplyReadiness(input: {
  daemonStatus: DaemonStatus;
  runtimeStatus: DaemonServiceRuntimeStatus | null;
}): DeviceReplyReadinessStatus {
  if (!input.daemonStatus.installed) {
    return {
      reasonCodes: ["service.not_installed"],
      reasonText: "WakeLoop Service is not installed on this device",
      replyReadiness: "blocked",
      updatedAt: new Date().toISOString(),
      wakeableRouteCount: 0,
    };
  }

  return {
    reasonCodes:
      input.runtimeStatus?.status === "stale" ? ["service.not_running"] : [],
    reasonText:
      input.runtimeStatus?.status === "stale"
        ? "WakeLoop Service is reconnecting on this device"
        : "WakeLoop couldn't confirm current reply readiness on this device",
    replyReadiness:
      input.runtimeStatus?.status === "stale" ? "degraded" : "unknown",
    updatedAt: new Date().toISOString(),
    wakeableRouteCount: 0,
  };
}

function enrichRuntimeStateWithManager(input: {
  runtimeState: DaemonServiceRuntimeStatus | null;
  serviceManager: DaemonServiceManager | null;
  serviceManagerStatus: DaemonServiceManagerRuntimeStatus | null;
}): DaemonServiceRuntimeStatus | null {
  if (!input.runtimeState) {
    return null;
  }
  return {
    ...input.runtimeState,
    managedBySystemService:
      input.runtimeState.managedBySystemService ||
      input.serviceManagerStatus?.installed === true,
    serviceController:
      input.runtimeState.serviceController === "unknown" &&
      input.serviceManager?.controller !== undefined
        ? input.serviceManager.controller
        : input.runtimeState.serviceController,
  };
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}
